import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GraduationCap, School, Search, X } from "lucide-react";
import {
  SCHOOL_CLASSES, COLLEGE_STREAMS, COLLEGE_SEMESTERS,
  SCHOOL_SUBJECTS, COLLEGE_SUBJECTS,
} from "@/lib/libraryTaxonomy";

export type LevelFilter = "school" | "college";

export interface FilterState {
  level: LevelFilter;
  category: string;
  semester: string;
  subject: string;
  query: string;
}

interface Props {
  value: FilterState;
  onChange: (next: FilterState) => void;
}

const ALL = "__all__";

const LibraryFilters = ({ value, onChange }: Props) => {
  const setLevel = (level: LevelFilter) => {
    onChange({ ...value, level, category: "", semester: "", subject: "" });
  };

  const categories = value.level === "school" ? SCHOOL_CLASSES : COLLEGE_STREAMS;
  const subjects = value.level === "school" ? SCHOOL_SUBJECTS : COLLEGE_SUBJECTS;

  const reset = () =>
    onChange({ level: value.level, category: "", semester: "", subject: "", query: "" });

  return (
    <div className="glass rounded-2xl p-4 md:p-5 space-y-4">
      <div className="flex items-center gap-2 p-1 rounded-xl bg-muted/60 w-fit">
        <Button
          size="sm"
          variant={value.level === "school" ? "hero" : "ghost"}
          onClick={() => setLevel("school")}
        >
          <School className="w-4 h-4 mr-1.5" /> School
        </Button>
        <Button
          size="sm"
          variant={value.level === "college" ? "hero" : "ghost"}
          onClick={() => setLevel("college")}
        >
          <GraduationCap className="w-4 h-4 mr-1.5" /> College
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Select
          value={value.category || ALL}
          onValueChange={(v) => onChange({ ...value, category: v === ALL ? "" : v })}
        >
          <SelectTrigger>
            <SelectValue placeholder={value.level === "school" ? "Class" : "Stream"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All {value.level === "school" ? "Classes" : "Streams"}</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {value.level === "college" ? (
          <Select
            value={value.semester || ALL}
            onValueChange={(v) => onChange({ ...value, semester: v === ALL ? "" : v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Semester" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Semesters</SelectItem>
              {COLLEGE_SEMESTERS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div />
        )}

        <Select
          value={value.subject || ALL}
          onValueChange={(v) => onChange({ ...value, subject: v === ALL ? "" : v })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Subject" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Subjects</SelectItem>
            {subjects.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search title, chapter…"
            value={value.query}
            onChange={(e) => onChange({ ...value, query: e.target.value })}
            className="pl-9"
          />
        </div>
      </div>

      {(value.category || value.semester || value.subject || value.query) && (
        <Button variant="ghost" size="sm" onClick={reset}>
          <X className="w-4 h-4 mr-1" /> Clear filters
        </Button>
      )}
    </div>
  );
};

export default LibraryFilters;
